import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to handle Game Bot logic.
 */
export const useGameBot = ({
  gameMode,
  makeMove,
  playerTurn,
  gameStarted,
  gameOver,
  botStrategy,
  gameState,
  difficulty,
  botPlayerId = 2,
  moveDelay = 1000,
  trigger = null,
}) => {
  const [isBotThinking, setIsBotThinking] = useState(false);
  
  // Refs to hold latest values to avoid stale closures in timeout
  // and prevent effect re-triggering just because an object identity changed.
  const stateRef = useRef(gameState);
  const strategyRef = useRef(botStrategy);
  const makeMoveRef = useRef(makeMove);

  useEffect(() => {
    stateRef.current = gameState;
    strategyRef.current = botStrategy;
    makeMoveRef.current = makeMove;
  }, [gameState, botStrategy, makeMove]);

  useEffect(() => {
    let timeoutId;

    if (
      gameStarted &&
      !gameOver &&
      gameMode === "PvC" &&
      playerTurn === botPlayerId
    ) {
      // Avoid scheduling if already thinking (though effect shouldn't re-run if isBotThinking changes due to deps below)
      // Actually, we WANT this effect to run when playerTurn changes to bot.
      
      setIsBotThinking(true);

      timeoutId = setTimeout(() => {
        try {
          // Use refs to get latest without relying on closure scope of this specific effect run
          const currentGameState = stateRef.current;
          const performMove = makeMoveRef.current;
          const strategy = strategyRef.current;

          const distinctMove = strategy(difficulty, currentGameState);
          if (distinctMove) {
            performMove(distinctMove);
          }
        } catch (error) {
          console.error("Bot failed to make a move:", error);
        } finally {
          setIsBotThinking(false);
        }
      }, moveDelay);
    }

    return () => clearTimeout(timeoutId);
  }, [
    gameMode,
    playerTurn,
    gameStarted,
    gameOver,
    difficulty,
    botPlayerId,
    moveDelay,
    trigger
  ]);

  return { isBotThinking };
};
